import { cloneElement, isValidElement, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import MonthRangePicker, {
  MonthRange,
  MonthRangePreset,
} from './MonthRangePicker';
import { usePeEmbed } from '../embedContext';

// ---------------------------------------------------------------------------
// PageSearchBar — shared search + filter row used at the top of every
// content page (Dashboard, Highlights & eBook, Project EDM, Upcoming
// / Commercial listings).
//
// Visually a full-width rounded search input on the left with a single
// outlined Filter button on the right. The Filter button highlights and
// shows the count when one or more filters are active.
//
// The bar never renders results itself. Pass `onSubmit` to hand Enter (and
// the trailing search button) off to the universal search results page —
// the one place in the app that shows search hits. Catalogue pages keep
// Filter / month-range for browsing; they do not also filter-as-you-type,
// so typing in this box has exactly one meaning.
//
// Pass `loading` to render a tiny spinner inside the input — useful when
// the page is debouncing the query and re-fetching in the background.
// ---------------------------------------------------------------------------

/**
 * A single applied filter, surfaced as a removable chip below the
 * search row. Each chip carries its own `onRemove` callback so the
 * parent can clear just that one filter without re-opening the
 * filter dialog. Tips for callers:
 *   - Use a short, human-readable label (e.g. "North East" rather
 *     than the raw filter value).
 *   - For multi-value filters (e.g. multiple projects), emit one
 *     chip per value so the user can remove them individually.
 *   - Set `group` to the filter's category (e.g. "Region", "District",
 *     "Property Type", "Project") so same-category chips are visually
 *     clustered together under a shared label. Chips without a `group`
 *     render standalone, in the order supplied.
 */
export interface AppliedFilterChip {
  id: string;
  label: string;
  onRemove: () => void;
  group?: string;
}

/** Props block for the optional month-range picker rendered next to Filter. */
export interface PageSearchBarMonthRange {
  value: MonthRange;
  onChange: (next: MonthRange) => void;
  presets?: MonthRangePreset[];
  /** Forwarded to `MonthRangePicker.buttonMinWidth`. */
  buttonMinWidth?: number;
}

export interface PageSearchBarProps {
  value: string;
  onChange: (v: string) => void;
  /** Active filter count shown on the Filter button. */
  filterCount?: number;
  /** Opens the filter dialog. When omitted, the Filter button is hidden. */
  onOpenFilters?: () => void;
  /** Override the search input placeholder. */
  placeholder?: string;
  /** Show a small spinner inside the input while a search/filter is in flight. */
  loading?: boolean;
  /**
   * Hands the trimmed query off to universal search. Wired to Enter and
   * to the trailing submit button. When omitted, both are inert.
   */
  onSubmit?: (query: string) => void;
  /**
   * Caption under the input. Defaults to a "search everything" hint when
   * `onSubmit` is set. Pass a custom string only when this bar is doing
   * something other than universal search.
   */
  filterHint?: string;
  /**
   * Optional month-range picker rendered between the search field
   * and the Filter button. When omitted, the picker is not shown.
   * Pages that opt in should keep the range in their own state and
   * pass `lastNMonthsRange(6)` (or similar) as the initial value.
   */
  monthRange?: PageSearchBarMonthRange;
  /**
   * Optional chip row rendered below the search row. Each chip
   * represents a single applied filter and exposes its own remove
   * action so the user can drop individual filters without opening
   * the filter dialog. When the array is empty (or omitted) the row
   * is not rendered at all — no wasted vertical space.
   */
  appliedFilters?: AppliedFilterChip[];
  /**
   * Optional handler for the "Clear all" link rendered at the end
   * of the chip row. Surface this only when removing every filter
   * is a useful single action (i.e. when 2+ filters are typically
   * applied at once). When omitted, only per-chip removal is shown.
   */
  onClearAllFilters?: () => void;
  /**
   * Optional dropdown rendered as a popper directly beneath the
   * search input. Shown when the input is focused (including empty
   * query for recent searches), or when the trimmed query is
   * non-empty / filters are active.
   */
  dropdownContent?: React.ReactNode;
  /**
   * Change this value to force-close the suggestions popper (e.g.
   * after a dropdown pick when staying on the same page).
   */
  dismissDropdownKey?: number | string;
  /**
   * Called when the user clears the search text via the input's X.
   * Use this on the results page to drop the committed `q` while
   * leaving filters alone. When omitted, only `onChange('')` runs.
   */
  onClearQuery?: () => void;
}

export default function PageSearchBar({
  value,
  onChange,
  filterCount = 0,
  onOpenFilters,
  placeholder = 'Search...',
  loading,
  dropdownContent,
  dismissDropdownKey,
  onSubmit,
  filterHint,
  monthRange,
  appliedFilters,
  onClearAllFilters,
  onClearQuery,
}: PageSearchBarProps) {
  const isEmbed = usePeEmbed();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const filterActive = filterCount > 0;
  const showFilterButton = typeof onOpenFilters === 'function';
  const canSubmit = typeof onSubmit === 'function' && value.trim().length > 0;
  const canClearQuery = value.length > 0;

  const inputAnchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [clickedAway, setClickedAway] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(440);

  useEffect(() => {
    if (dismissDropdownKey == null) return;
    setClickedAway(true);
  }, [dismissDropdownKey]);

  // Keep the suggestion panel open after the soft keyboard dismisses
  // (input blur) as long as there is a query / active filter — otherwise
  // tapping "Done" on iOS/Android collapses results under the keyboard
  // with nothing left to scroll. Click-away / Enter submit still close it.
  // Focus with an empty query still opens the panel so recent searches
  // can show.
  const showDropdown =
    Boolean(dropdownContent) &&
    !clickedAway &&
    (focused || value.trim().length > 0 || filterActive);

  // Size the dropdown to the visible viewport below the search bar so
  // results remain scrollable above the mobile soft keyboard.
  useEffect(() => {
    if (!showDropdown) return;

    const updateMaxHeight = () => {
      const el = inputAnchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const spaceBelow = viewportBottom - rect.bottom - 8;
      const capped = isMobile ? Math.min(spaceBelow, 360) : Math.min(spaceBelow, 440);
      setDropdownMaxHeight(Math.max(140, capped));
    };

    updateMaxHeight();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', updateMaxHeight);
    vv?.addEventListener('scroll', updateMaxHeight);
    window.addEventListener('resize', updateMaxHeight);
    return () => {
      vv?.removeEventListener('resize', updateMaxHeight);
      vv?.removeEventListener('scroll', updateMaxHeight);
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, [showDropdown, isMobile, value, filterActive, focused]);

  const submit = () => {
    if (!onSubmit) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    setClickedAway(true);
    inputRef.current?.blur();
    onSubmit(trimmed);
  };

  const clearQuery = () => {
    onChange('');
    onClearQuery?.();
  };

  const dropdownNode =
    dropdownContent && isValidElement(dropdownContent)
      ? cloneElement(dropdownContent as ReactElement<{ maxHeight?: number }>, {
          maxHeight: dropdownMaxHeight,
        })
      : dropdownContent;

  // Cluster chips by `group` (preserving first-seen order) so same-
  // category filters (e.g. every selected District) render together
  // under one label instead of an unordered flat list. Chips without
  // a `group` fall into their own single-chip bucket and render with
  // no wrapper label, matching the original flat behaviour.
  const chipGroups = useMemo(() => {
    const buckets: Array<{ key: string; label: string | null; chips: AppliedFilterChip[] }> = [];
    const indexByKey = new Map<string, number>();
    for (const chip of appliedFilters ?? []) {
      const key = chip.group ?? `__solo:${chip.id}`;
      let idx = indexByKey.get(key);
      if (idx == null) {
        idx = buckets.length;
        indexByKey.set(key, idx);
        buckets.push({ key, label: chip.group ?? null, chips: [] });
      }
      buckets[idx].chips.push(chip);
    }
    return buckets;
  }, [appliedFilters]);

  return (
    // Sticky wrapper so the search + filter row stays in view while
    // the user scrolls the page. Anchored just below the layout's
    // sticky brand header (≈56–60px tall depending on viewport — see
    // `ProjectEssentialLayout`'s header `py: 1.25` + brand cluster).
    // `zIndex` sits below the header's 10 so a tap on the header
    // still wins, but above the page content so this bar always
    // paints over it. Horizontal margin/padding stay 0 — the wrapper
    // inherits the surrounding `<main>` content padding so it spans
    // the same width as everything else.
    <Box
      ref={inputAnchorRef}
      sx={{
        position: 'sticky',
        top: isEmbed ? 0 : { xs: 56, sm: 60 },
        zIndex: 5,
        bgcolor: 'var(--color-bg)',
        py: isEmbed ? 0.75 : 1,
        mt: isEmbed ? 0 : -1,
        mx: isEmbed ? { xs: -0.5, sm: 0 } : 0,
        borderBottom: '1px solid transparent',
        transition: 'border-color 150ms ease',
        '@supports (position: sticky)': {
          borderBottomColor: 'var(--color-border)',
        },
      }}
    >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', sm: 'stretch' }}
    >
      {/* Keep the input and the portaled popper under one ClickAwayListener
          so the focusing click that opens recent searches is not treated
          as an outside click (which briefly flashed then hid the panel). */}
      <ClickAwayListener onClickAway={() => setClickedAway(true)}>
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Box
            component="form"
            // Native form submit is what iOS "Go" / "Search" reliably fires;
            // keydown Enter alone is flaky on mobile Safari.
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            sx={{ width: '100%' }}
          >
            <TextField
              fullWidth
              size="small"
              type="search"
              placeholder={placeholder}
              value={value}
              inputRef={inputRef}
              onChange={(e) => {
                if (clickedAway) setClickedAway(false);
                onChange(e.target.value);
              }}
              onFocus={() => {
                setFocused(true);
                setClickedAway(false);
              }}
              onBlur={() => setFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setClickedAway(true);
              }}
              inputProps={{
                enterKeyHint: onSubmit ? 'search' : undefined,
                autoCapitalize: 'off',
                autoCorrect: 'off',
                spellCheck: false,
                // Hide native WebKit search clear so we keep a single clear control.
                sx: undefined,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'var(--color-text-muted)' }} fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {loading && (
                        <CircularProgress
                          size={16}
                          thickness={5}
                          sx={{ color: 'var(--color-accent)' }}
                        />
                      )}
                      {canClearQuery && (
                        <Tooltip title="Clear search text">
                          <IconButton
                            size="small"
                            type="button"
                            aria-label="Clear search text"
                            onClick={clearQuery}
                            sx={{ color: 'var(--color-text-muted)' }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onSubmit && (
                        <Tooltip title="Search all of Project Essentials">
                          <span>
                            <IconButton
                              size="small"
                              type="submit"
                              aria-label="Search all of Project Essentials"
                              disabled={!canSubmit}
                              sx={{
                                color: canSubmit
                                  ? 'var(--color-accent-strong)'
                                  : 'var(--color-text-muted)',
                              }}
                            >
                              <SearchIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: 'var(--color-surface)',
                  borderRadius: 2,
                  // WebKit adds an extra clear button for type=search.
                  '& input[type="search"]::-webkit-search-cancel-button': {
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  },
                  '& input[type="search"]::-webkit-search-decoration': {
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--color-border)',
                },
              }}
            />
          </Box>
          {dropdownContent && (
            <Popper
              open={showDropdown}
              anchorEl={inputAnchorRef.current}
              placement="bottom-start"
              sx={{
                zIndex: 6,
                width: inputAnchorRef.current?.offsetWidth,
                mt: 0.5,
              }}
              modifiers={[{ name: 'preventOverflow', options: { padding: 8 } }]}
            >
              <Box>{dropdownNode}</Box>
            </Popper>
          )}
        </Box>
      </ClickAwayListener>
      {monthRange && (
        <MonthRangePicker
          value={monthRange.value}
          onChange={monthRange.onChange}
          presets={monthRange.presets}
          buttonMinWidth={monthRange.buttonMinWidth}
        />
      )}
      {showFilterButton && (
        <Button
          variant="outlined"
          type="button"
          startIcon={<TuneIcon />}
          onClick={onOpenFilters}
          sx={{
            flexShrink: 0,
            borderRadius: 2,
            textTransform: 'none',
            borderColor: filterActive ? 'var(--color-accent)' : 'var(--color-border-strong)',
            color: filterActive ? 'var(--color-accent-strong)' : 'var(--color-text-strong)',
            bgcolor: filterActive ? 'var(--color-accent-soft)' : 'var(--color-surface)',
            fontWeight: 600,
            minWidth: { xs: 96, sm: 110 },
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          Filter{filterActive ? ` (${filterCount})` : ''}
        </Button>
      )}
    </Stack>
    {/* One caption, one meaning. Universal search is the only thing this
        input does when `onSubmit` is wired — catalogue pages no longer
        also filter themselves as you type. Hide on mobile while the
        dropdown is open to reclaim space above the keyboard. */}
    {(filterHint || onSubmit) && !(isMobile && showDropdown) && (
      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mt: 0.75, display: 'block' }}>
        {filterHint || 'Press Enter to search projects, EDMs, eBooks and reports'}
      </Typography>
    )}
    {/* Applied-filter chip row — surfaces every active filter as a
        removable chip so the user can see what's narrowing the
        results without opening the filter dialog, and drop
        individual filters with one click. Only rendered when at
        least one chip is supplied. */}
    {appliedFilters && appliedFilters.length > 0 && (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mt: 1,
        }}
      >
        {chipGroups.map((group) => (
          <Box
            key={group.key}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              ...(group.label
                ? {
                    py: 0.5,
                    pl: 1,
                    pr: 0.75,
                    borderRadius: 999,
                    bgcolor: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-border)',
                  }
                : null),
            }}
          >
            {group.label && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.68rem',
                  color: 'var(--color-text-muted)',
                  mr: 0.25,
                  whiteSpace: 'nowrap',
                }}
              >
                {group.label}
              </Typography>
            )}
            {group.chips.map((chip) => (
              <Chip
                key={chip.id}
                label={chip.label}
                onDelete={chip.onRemove}
                deleteIcon={<CloseIcon style={{ fontSize: 14 }} />}
                size="small"
                sx={{
                  borderRadius: 999,
                  bgcolor: 'var(--color-accent-soft)',
                  color: 'var(--color-accent-strong)',
                  border: '1px solid var(--color-accent)',
                  fontWeight: 600,
                  '& .MuiChip-deleteIcon': {
                    color: 'var(--color-accent-strong)',
                    '&:hover': { color: 'var(--color-accent)' },
                  },
                }}
              />
            ))}
          </Box>
        ))}
        {onClearAllFilters && appliedFilters.length > 1 && (
          <Button
            size="small"
            onClick={onClearAllFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'var(--color-text-muted)',
              minWidth: 0,
              px: 1,
              '&:hover': {
                color: 'var(--color-text-strong)',
                bgcolor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Clear all
          </Button>
        )}
      </Box>
    )}
    </Box>
  );
}
